import {
  ECONSENT_STATUS_CHANGED_EVENT,
  EConsentStatusChangedDetail,
} from '@/constants/econsent';
import { hasUserProfile } from '@/constants/roles';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { AppUserInfo } from '@/models/AppViewModel';
import { ModuleDefinition, useModuleAccess } from '@/permissions';
import { useGetEConsentStatusMutation } from '@/store/eConsentApi';
import {
  resolveRuntimeEConsentConfig,
  resolveDefaultEConsentPromptWindowConfig,
  resolveEConsentPromptTitle,
} from '@/store/eConsentConfigApi';
import { WARootState } from '@/store/store';
import { useGetUserProfileQuery } from '@/store/usersApi';
import { deriveAdvisorGroupId } from '@/utils/deriveAdvisorGroupId';
import { hasVisibleHtmlContent } from '@/utils/htmlContent';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import { skipToken } from '@reduxjs/toolkit/query';
import { t } from 'i18next';
import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import EConsentUtility from './econsent.utility';

export interface EConsentAppUserInfo extends AppUserInfo {
  ExternalId?: string | undefined;
}

export const useEConsent = () => {
  const { isModuleEnabled } = useModuleAccess();
  const authLoaded = useAuthIsLoaded();

  const userInfo = useSelector<WARootState, EConsentAppUserInfo | undefined>(
    (state) => state.appView.UserInfo,
  );
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );

  const advisorGroupId = useMemo(
    () => deriveAdvisorGroupId(userInfo),
    [userInfo],
  );

  const eConsentConfig = useMemo(
    () =>
      advisorGroupId !== null
        ? resolveRuntimeEConsentConfig(advisorGroupId, userInfo)
        : null,
    [advisorGroupId, userInfo],
  );

  const [isEConsentQuickLinkEligible, setIsEConsentQuickLinkEligible] =
    useState(false);

  const showEConsentButton =
    (eConsentConfig?.showEconsentButton ?? false) &&
    !(isCapacitorApp() && !(eConsentConfig?.showEconsentOnMobile ?? true));

  const isQuickLinkModuleVisible = useCallback(
    (module: ModuleDefinition) => {
      if (module.moduleKey === 'econsent2') {
        return (
          isModuleEnabled(module.moduleKey) &&
          showEConsentButton &&
          isEConsentQuickLinkEligible
        );
      }

      return isModuleEnabled(module.moduleKey);
    },
    [isEConsentQuickLinkEligible, isModuleEnabled, showEConsentButton],
  );

  const defaultEConsentPromptWindowConfig = useMemo(
    () => resolveDefaultEConsentPromptWindowConfig(),
    [],
  );

  const [eConsentQuickLinkTitle, setEConsentQuickLinkTitle] = useState(
    resolveEConsentPromptTitle('', defaultEConsentPromptWindowConfig.title),
  );

  const getModuleLabel = useCallback(
    (module: ModuleDefinition) => {
      if (module.moduleKey === 'econsent2') {
        return eConsentQuickLinkTitle;
      }

      return t(module.titleKey);
    },
    [eConsentQuickLinkTitle],
  );

  const eConsentStatusRequestKeyRef = useRef<string | null>(null);
  const submittedEConsentStatusRef = useRef<EConsentStatusChangedDetail | null>(
    null,
  );
  const [getEConsentStatus] = useGetEConsentStatusMutation();

  const shouldLoadEConsentProfile =
    authLoaded &&
    hasUserProfile(currentRole) &&
    isModuleEnabled('econsent2') &&
    showEConsentButton &&
    Boolean(userInfo?.Token) &&
    advisorGroupId !== null;

  const profileArg = shouldLoadEConsentProfile ? undefined : skipToken;

  const {
    data: eConsentProfile,
    isLoading: isLoadingEConsentProfile,
    isFetching: isFetchingEConsentProfile,
  } = useGetUserProfileQuery(profileArg);

  useEffect(() => {
    const handleEConsentStatusChanged = (event: Event) => {
      const detail = (event as CustomEvent<EConsentStatusChangedDetail>).detail;
      if (!detail.submitted) {
        return;
      }

      const isCurrentUser =
        detail.userId === undefined || detail.userId === userInfo?.UserId;
      const isCurrentAdvisorGroup =
        detail.advisorGroupId === undefined ||
        detail.advisorGroupId === advisorGroupId;

      if (!isCurrentUser || !isCurrentAdvisorGroup) {
        return;
      }

      submittedEConsentStatusRef.current = detail;
      setIsEConsentQuickLinkEligible(false);
      setEConsentQuickLinkTitle(
        resolveEConsentPromptTitle('', defaultEConsentPromptWindowConfig.title),
      );
      eConsentStatusRequestKeyRef.current = null;
    };

    window.addEventListener(
      ECONSENT_STATUS_CHANGED_EVENT,
      handleEConsentStatusChanged,
    );

    return () => {
      window.removeEventListener(
        ECONSENT_STATUS_CHANGED_EVENT,
        handleEConsentStatusChanged,
      );
    };
  }, [
    advisorGroupId,
    defaultEConsentPromptWindowConfig.title,
    userInfo?.UserId,
  ]);

  useEffect(() => {
    if (!authLoaded || !isModuleEnabled('econsent2') || !showEConsentButton) {
      setIsEConsentQuickLinkEligible(false);
      setEConsentQuickLinkTitle(
        resolveEConsentPromptTitle('', defaultEConsentPromptWindowConfig.title),
      );
      eConsentStatusRequestKeyRef.current = null;
      return;
    }

    if (isLoadingEConsentProfile || isFetchingEConsentProfile) {
      return;
    }

    const request = EConsentUtility.buildConsentStatusRequest(
      userInfo,
      advisorGroupId,
      eConsentProfile,
    );
    if (!request) {
      return;
    }

    const requestKey = JSON.stringify(request);
    if (
      EConsentUtility.doesSubmittedStatusMatchRequest(
        submittedEConsentStatusRef.current,
        request,
      )
    ) {
      setIsEConsentQuickLinkEligible(false);
      return;
    }

    if (eConsentStatusRequestKeyRef.current === requestKey) {
      return;
    }

    eConsentStatusRequestKeyRef.current = requestKey;

    void getEConsentStatus(request)
      .unwrap()
      .then((response) => {
        if (
          EConsentUtility.doesSubmittedStatusMatchRequest(
            submittedEConsentStatusRef.current,
            request,
          )
        ) {
          setIsEConsentQuickLinkEligible(false);
          return;
        }

        setEConsentQuickLinkTitle(
          resolveEConsentPromptTitle(
            response.promptActionLabel,
            defaultEConsentPromptWindowConfig.title,
          ),
        );
        setIsEConsentQuickLinkEligible(
          !response.submitted && hasVisibleHtmlContent(response.description),
        );
      })
      .catch(() => {
        setEConsentQuickLinkTitle(
          resolveEConsentPromptTitle(
            '',
            defaultEConsentPromptWindowConfig.title,
          ),
        );
        setIsEConsentQuickLinkEligible(false);
      });
  }, [
    advisorGroupId,
    authLoaded,
    defaultEConsentPromptWindowConfig.title,
    eConsentProfile,
    getEConsentStatus,
    isFetchingEConsentProfile,
    isLoadingEConsentProfile,
    isModuleEnabled,
    showEConsentButton,
    userInfo,
  ]);

  return { getModuleLabel, isQuickLinkModuleVisible };
};
